/**
 * Enrolamiento TOTP -- primera vez que un usuario con `auth.require_mfa`
 * activo (apagado por defecto, ver README del backend) inicia sesión sin
 * tener el segundo factor configurado todavía. Llega aquí via
 * RequireSession -> guards.tsx, que redirige cuando `mfa === 'not_enrolled'`.
 *
 * Sin generación de QR (evita sumar una librería solo para esto): se
 * muestra el secreto en texto para ingreso manual -- cualquier app TOTP
 * (Google Authenticator, Authy, 1Password, etc.) lo acepta igual que un QR.
 *
 * Los rechazos se explican (lib/mfa.ts): sesión de verificación vencida, código vencido, mal formado o equivocado.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { mfaEnrollConfirm, mfaEnrollStart, type MfaEnrollStart } from '../lib/backend';
import { CLEARS_CODE, CODE_LENGTH, classifyMfaError, type MfaFailure } from '../lib/mfa';
import { CodeField, SessionExpired } from '../components/MfaShared';
import { Wordmark } from '../components/Logo';

export function MfaEnroll() {
  const { pendingMfaToken, completeMfa } = useAuth();
  const nav = useNavigate();
  const [start, setStart] = useState<MfaEnrollStart | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [failure, setFailure] = useState<MfaFailure | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pendingMfaToken) return;
    mfaEnrollStart(pendingMfaToken)
      .then(setStart)
      .catch((e) => {
        const f = classifyMfaError(e);
        if (f.kind === 'session_expired') setFailure(f);
        else setLoadErr(f.kind === 'network' || f.kind === 'already_enrolled' ? f.message : 'No se pudo generar el segundo factor. Intente iniciar sesión nuevamente.');
      });
  }, [pendingMfaToken]);

  if (!pendingMfaToken || failure?.kind === 'session_expired') return <SessionExpired message={failure?.message} />;

  async function confirm() {
    if (!pendingMfaToken) return;
    setBusy(true);
    setFailure(null);
    try {
      const token = await mfaEnrollConfirm(pendingMfaToken, code.trim());
      await completeMfa(token);
      nav('/', { replace: true });
    } catch (e) {
      // Se dice POR QUE fallo: token vencido, código vencido / mal formado / equivocado, falta iniciar la inscripción...
      const f = classifyMfaError(e);
      setFailure(f);
      if (CLEARS_CODE.has(f.kind)) {
        setCode('');
        document.getElementById('mfa-code')?.focus();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authwrap">
      <div className="authcard" style={{ maxWidth: 420 }}>
        <div className="authbrand">
          <Wordmark size={38} />
        </div>
        <h2 style={{ fontSize: 19, marginBottom: 4 }}>Configurar verificación en dos pasos</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 18 }}>
          Esta institución exige un segundo factor de autenticación. Configúrelo una sola vez con
          una aplicación como Google Authenticator, Authy o 1Password.
        </p>

        {loadErr && <div className="callout callout--crit" style={{ marginBottom: 14 }}>{loadErr}</div>}

        {start && (
          <>
            <div className="field">
              <label>1. Agregue esta clave en su aplicación de autenticación</label>
              <div
                className="mono"
                style={{
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--ground)',
                  fontSize: 14,
                  letterSpacing: 1,
                  wordBreak: 'break-all',
                }}
              >
                {start.secret}
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Cuenta nueva → "Ingresar clave de configuración manualmente" → pegue esta clave.
              </p>
            </div>

            <CodeField
              id="mfa-code"
              label="2. Ingrese el código de 6 dígitos que muestra la aplicación"
              value={code}
              onChange={(c) => {
                setCode(c);
                setFailure(null);
              }}
              error={failure?.message}
            />

            <button
              className="btn btn--primary"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={busy || code.length !== CODE_LENGTH}
              onClick={() => void confirm()}
            >
              {busy ? 'Verificando…' : 'Confirmar y continuar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
